import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, upload } from '../lib/api.ts';
import { BudgetLineBox, BudgetLineSelect, commitToLine, useBudgetFor } from '../lib/budget.tsx';
import { dateTime, day, money, parseMoney, todayIso } from '../lib/format.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Procurement and contracts. A procedure is prepared, published with a deadline, its offers are
 * recorded sealed and opened after the deadline, evaluated and awarded; the award becomes a
 * contract with its value and dates, changed only by amendments, invoiced until it is completed.
 */

type Kind = 'small_value' | 'quotes' | 'open' | 'restricted' | 'negotiated' | 'other';
type Criterion = 'lowest_price' | 'best_value';
type PState = 'draft' | 'published' | 'evaluation' | 'awarded' | 'contracted' | 'cancelled';
type BState = 'received' | 'valid' | 'rejected' | 'withdrawn';
type KState = 'draft' | 'active' | 'completed' | 'terminated';
type Tab = 'procedures' | 'contracts';
type PView = 'open' | 'receiving' | 'to_open' | 'evaluation' | 'done' | 'all';
type KView = 'active' | 'ending' | 'draft' | 'done' | 'all';

interface Overview {
  today: string;
  procedures: { open: number; receiving: number; to_open: number; evaluating: number; estimated: number };
  contracts: { active: number; value: number; ending: number; overdue: number; drafts: number; guarantees: number; invoiced: number };
  years: number[];
  can: { manage: boolean; award: boolean };
  purchasing: boolean;
  budget: boolean;
}
interface ProcRow {
  id: string;
  name: string;
  title: string;
  kind: Kind;
  criterion: Criterion;
  estimated: number;
  responsible_name: string | null;
  deadline: string | null;
  winner: string | null;
  state: PState;
  bids: number;
}
interface Bid {
  id: string;
  supplier_name: string;
  supplier: string | null;
  tax_id: string | null;
  received_at: string;
  amount: number | null;
  score: number | null;
  state: BState;
  reason: string | null;
  rank: number | null;
}
interface HistoryRow {
  id: string;
  kind: string;
  text: string | null;
  data: Record<string, any> | null;
  at: string;
  by_name: string | null;
}
interface FileRow {
  id: string;
  name: string;
  mime: string;
  size: number;
  at: string;
  by_name: string | null;
}
interface ProcDetail {
  procedure: ProcRow & {
    number: number;
    year: number;
    description: string | null;
    responsible: string | null;
    published_on: string | null;
    opened_at: string | null;
    award_reason: string | null;
    awarded_on: string | null;
    cancel_reason: string | null;
  };
  bids: Bid[];
  contract: { id: string; name: string; state: KState } | null;
  history: HistoryRow[];
  files: FileRow[];
  deadline_passed: boolean;
  can: { manage: boolean; award: boolean };
}
interface ContractRow {
  id: string;
  name: string;
  title: string;
  supplier_name: string;
  supplier: string | null;
  tax_id: string | null;
  value: number;
  invoiced: number;
  left: number;
  starts: string | null;
  ends: string | null;
  signed_on: string | null;
  guarantee: number;
  guarantee_until: string | null;
  state: KState;
  ending: boolean;
  late: boolean;
}
interface ContractDetail {
  contract: ContractRow & {
    procedure: string | null;
    responsible: string | null;
    responsible_name: string | null;
    note: string | null;
    end_reason: string | null;
    ended_on: string | null;
    original: number;
  };
  procedure: { id: string; name: string; title: string } | null;
  amendments: { id: string; number: number; date: string; value_change: number; new_end: string | null; reason: string; reference: string | null }[];
  invoices: { id: string; date: string; label: string; amount: number; bill: string | null; cancelled: boolean }[];
  bills: { id: string; number: number; date: string; total: number; supplier_ref: string | null }[];
  history: HistoryRow[];
  files: FileRow[];
  can: { manage: boolean; award: boolean };
}
interface Options {
  people: { id: string; name: string }[];
  suppliers: { id: string; name: string; tax_id: string | null }[];
}

const KINDS: Kind[] = ['small_value', 'quotes', 'open', 'restricted', 'negotiated', 'other'];
const P_VIEWS: PView[] = ['open', 'receiving', 'to_open', 'evaluation', 'done', 'all'];
const K_VIEWS: KView[] = ['active', 'ending', 'draft', 'done', 'all'];
const P_STATE: Record<PState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  published: { tone: 'info', icon: 'send' },
  evaluation: { tone: 'warn', icon: 'eye' },
  awarded: { tone: 'primary', icon: 'star' },
  contracted: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const B_STATE: Record<BState, { tone: Tone; icon: IconName }> = {
  received: { tone: 'info', icon: 'lock' },
  valid: { tone: 'ok', icon: 'check' },
  rejected: { tone: 'danger', icon: 'x' },
  withdrawn: { tone: 'neutral', icon: 'undo' },
};
const K_STATE: Record<KState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  active: { tone: 'ok', icon: 'check' },
  completed: { tone: 'info', icon: 'check' },
  terminated: { tone: 'danger', icon: 'x' },
};
const MAX_FILE = 8_000_000;
const ACCEPT = 'application/pdf,image/jpeg,image/png';
/** History lines whose own text (a reason) is worth showing under the title. */
const WITH_TEXT = new Set(['bid_withdrawn', 'rejected', 'awarded', 'cancelled', 'amended', 'invoice_cancelled', 'completed', 'terminated']);

/** Reasons the server writes itself, shown in the person's language. */
const LATE = 'Arrived after the deadline.';
const OWN_TEXT: Record<string, MessageKey> = { [LATE]: 'prc.late_reason', 'The bill was cancelled in Purchasing.': 'prc.bill_cancelled_reason' };
const ownText = (text: string | null) => (text && OWN_TEXT[text] ? t(OWN_TEXT[text]) : text);
const lek = (minor: number) => (minor ? (minor / 100).toFixed(2).replace(/\.00$/, '') : '');
const kindLabel = (k: Kind) => t(`prc.kind_${k}` as MessageKey);
const pad = (n: number) => String(n).padStart(2, '0');
/** A moment as the value of a datetime-local input, in the browser's time. */
const localInput = (at: Date) => `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
const inDays = (n: number, hour = 12) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  d.setHours(hour, 0, 0, 0);
  return localInput(d);
};

function PBadge({ s, late }: { s: PState; late?: boolean }) {
  if (late) return <StatusBadge tone="warn" icon="clock" label={t('prc.to_open')} />;
  return <StatusBadge tone={P_STATE[s].tone} icon={P_STATE[s].icon} label={t(`prc.ps_${s}` as MessageKey)} />;
}
function KBadge({ k }: { k: Pick<ContractRow, 'state' | 'late' | 'ending'> }) {
  if (k.late) return <StatusBadge tone="danger" icon="alert" label={t('prc.k_late_badge')} />;
  if (k.ending) return <StatusBadge tone="warn" icon="clock" label={t('prc.k_ending_badge')} />;
  return <StatusBadge tone={K_STATE[k.state].tone} icon={K_STATE[k.state].icon} label={t(`prc.ks_${k.state}` as MessageKey)} />;
}
function Progress({ value, done }: { value: number; done: number }) {
  const pct = value > 0 ? Math.min(100, Math.round((done / value) * 100)) : 0;
  return (
    <span className="prc-prog">
      <span className="prj-bar" role="img" aria-label={t('prc.invoiced_pct', { pct })}>
        <span style={{ width: `${pct}%` }} />
      </span>
      <span className="tabular small">{pct}%</span>
    </span>
  );
}

export function Procurement() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'contracts' ? 'contracts' : 'procedures';
  const pView = (P_VIEWS.includes(params.get('view') as PView) ? params.get('view') : 'open') as PView;
  const kView = (K_VIEWS.includes(params.get('view') as KView) ? params.get('view') : 'active') as KView;
  const year = Number(params.get('year')) || null;
  const [q, setQ] = useState(params.get('q') ?? '');
  const [query, setQuery] = useState(q);
  const { data: ov, reload: reloadOv } = useAction<Overview>('procurement.overview');
  const { data: procs, reload: reloadP } = useAction<ProcRow[]>('procurement.procedures', { view: pView, year, q: query || null });
  const { data: contracts, reload: reloadK } = useAction<ContractRow[]>('procurement.contracts', { view: kView, year, q: query || null });
  const [form, setForm] = useState<null | { kind: 'procedure' } | { kind: 'contract'; from?: ProcDetail }>(null);
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const changed = async () => {
    await Promise.all([reloadOv(), reloadP(), reloadK()]);
  };
  const openP = params.get('p');
  const openK = params.get('k');
  const pCount = (v: PView) => (!ov ? 0 : v === 'open' ? ov.procedures.open : v === 'receiving' ? ov.procedures.receiving : v === 'to_open' ? ov.procedures.to_open : v === 'evaluation' ? ov.procedures.evaluating : 0);
  const kCount = (v: KView) => (!ov ? 0 : v === 'active' ? ov.contracts.active : v === 'ending' ? ov.contracts.ending + ov.contracts.overdue : v === 'draft' ? ov.contracts.drafts : 0);
  const exportCsv = () => {
    if (tab === 'procedures' && procs) {
      downloadCsv(
        'procedures.csv',
        [t('prc.c_no'), t('prc.c_subject'), t('prc.kind'), t('prc.c_estimated'), t('prc.c_deadline'), t('prc.c_offers'), t('prc.winner'), t('prc.c_state')],
        procs.map((r) => [r.name, r.title, kindLabel(r.kind), lek(r.estimated) || '0', r.deadline ? dateTime(r.deadline) : '', String(r.bids), r.winner ?? '', t(`prc.ps_${r.state}` as MessageKey)]),
      );
    }
    if (tab === 'contracts' && contracts) {
      downloadCsv(
        'contracts.csv',
        [t('prc.c_no'), t('prc.c_subject'), t('prc.supplier'), t('prc.nipt'), t('prc.c_value'), t('prc.c_invoiced'), t('prc.starts'), t('prc.c_ends'), t('prc.c_state')],
        contracts.map((r) => [r.name, r.title, r.supplier_name, r.tax_id ?? '', lek(r.value), lek(r.invoiced) || '0', r.starts ?? '', r.ends ?? '', t(`prc.ks_${r.state}` as MessageKey)]),
      );
    }
  };

  return (
    <Shell>
      <div className="page reports procurement">
        <div className="page-head">
          <div>
            <h1>{t('prc.title')}</h1>
            <p className="muted">{t('prc.subtitle')}</p>
          </div>
          <div className="page-actions">
            <Button icon="download" onClick={exportCsv}>
              {t('prc.export')}
            </Button>
            {ov?.can.manage ? (
              <>
                <Button icon="invoice" onClick={() => setForm({ kind: 'contract' })}>
                  {t('prc.new_contract')}
                </Button>
                <Button variant="primary" icon="plus" onClick={() => setForm({ kind: 'procedure' })}>
                  {t('prc.new_procedure')}
                </Button>
              </>
            ) : null}
          </div>
        </div>
        {!ov ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('prc.at_a_glance')}>
            <Kpi label={t('prc.k_open')} value={String(ov.procedures.open)} hint={t('prc.k_open_hint', { amount: money(ov.procedures.estimated) })} />
            <Kpi
              label={t('prc.k_to_open')}
              value={String(ov.procedures.to_open + ov.procedures.evaluating)}
              hint={ov.procedures.to_open ? tn('prc.k_to_open_hint', ov.procedures.to_open) : tn('prc.k_receiving', ov.procedures.receiving)}
              warn={ov.procedures.to_open > 0}
            />
            <Kpi label={t('prc.k_contracts')} value={money(ov.contracts.value)} hint={t('prc.k_contracts_hint', { n: ov.contracts.active, amount: money(ov.contracts.invoiced) })} />
            <Kpi
              label={t('prc.k_ending')}
              value={String(ov.contracts.ending + ov.contracts.overdue)}
              hint={
                ov.contracts.overdue
                  ? tn('prc.k_overdue', ov.contracts.overdue)
                  : ov.contracts.guarantees
                    ? tn('prc.k_guarantees', ov.contracts.guarantees)
                    : ov.contracts.ending
                      ? t('prc.k_ending_some')
                      : t('prc.k_ending_hint')
              }
              warn={ov.contracts.overdue + ov.contracts.ending > 0}
            />
          </section>
        )}

        <div className="segmented tabs prc-main-tabs" role="tablist" aria-label={t('prc.title')}>
          {(['procedures', 'contracts'] as Tab[]).map((k) => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => set({ tab: k === 'procedures' ? null : k, view: null })}>
              <Icon name={k === 'procedures' ? 'list' : 'invoice'} size={16} />
              {t(`prc.tab_${k}` as MessageKey)}
            </button>
          ))}
        </div>

        <div className="toolbar prc-toolbar">
          <div className="segmented tabs" role="tablist" aria-label={t('prc.show')}>
            {(tab === 'procedures' ? P_VIEWS : K_VIEWS).map((v) => {
              const active = !query && (tab === 'procedures' ? pView : kView) === v;
              const n = tab === 'procedures' ? pCount(v as PView) : kCount(v as KView);
              return (
                <button key={v} role="tab" aria-selected={active} className={active ? 'active' : ''} onClick={() => (setQ(''), set({ view: v === (tab === 'procedures' ? 'open' : 'active') ? null : v }))}>
                  {t(`prc.${tab === 'procedures' ? 'pv' : 'kv'}_${v}` as MessageKey)}
                  {n ? <span className="count">{n}</span> : null}
                </button>
              );
            })}
          </div>
          <span className="row-actions">
            <label className="search">
              <Icon name="search" size={16} />
              <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t(tab === 'procedures' ? 'prc.search_p' : 'prc.search_k')} aria-label={t(tab === 'procedures' ? 'prc.search_p' : 'prc.search_k')} />
            </label>
            {ov && ov.years.length > 1 ? (
              <select className="input" value={year ?? ''} onChange={(e) => set({ year: e.target.value || null })} aria-label={t('prc.year')}>
                <option value="">{t('prc.all_years')}</option>
                {ov.years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            ) : null}
          </span>
        </div>

        {tab === 'procedures' ? (
          !procs ? (
            <Skeleton h={300} r="var(--radius-lg)" />
          ) : !procs.length ? (
            query ? (
              <EmptyState icon="search" title={t('prc.none_found')} body={t('prc.none_found_body', { q: query })} />
            ) : (
              <EmptyState
                icon="list"
                title={t(`prc.empty_p_${pView}` as MessageKey)}
                body={pView === 'open' ? t('prc.empty_p_body') : undefined}
                action={
                  pView === 'open' && ov?.can.manage ? (
                    <Button variant="primary" icon="plus" onClick={() => setForm({ kind: 'procedure' })}>
                      {t('prc.new_procedure')}
                    </Button>
                  ) : undefined
                }
              />
            )
          ) : (
            <Table
              view="procurement.procedures"
              head={[t('prc.c_no'), t('prc.c_subject'), t('prc.c_deadline'), t('prc.c_estimated'), t('prc.c_offers'), t('prc.c_state')]}
              phoneHide={[2, 3, 4]}
              left={[0, 1]}
              rows={procs.map((r) => {
                const late = r.state === 'published' && !!r.deadline && new Date(r.deadline) <= new Date();
                return {
                  key: r.id,
                  onClick: () => set({ p: r.id }),
                  cells: [
                    <span className="tabular strong">{r.name}</span>,
                    <span className="vat-party">
                      <span>{r.title}</span>
                      <span className="muted">{[kindLabel(r.kind), r.winner ? t('prc.won_by', { name: r.winner }) : r.responsible_name].filter(Boolean).join(' · ')}</span>
                    </span>,
                    <span className="tabular">{r.deadline ? dateTime(r.deadline) : '—'}</span>,
                    <span className="tabular">{money(r.estimated)}</span>,
                    <span className="tabular">{r.bids || '—'}</span>,
                    <PBadge s={r.state} late={late} />,
                  ],
                };
              })}
            />
          )
        ) : !contracts ? (
          <Skeleton h={300} r="var(--radius-lg)" />
        ) : !contracts.length ? (
          query ? (
            <EmptyState icon="search" title={t('prc.none_found')} body={t('prc.none_found_body', { q: query })} />
          ) : (
            <EmptyState icon="invoice" title={t(`prc.empty_k_${kView}` as MessageKey)} body={kView === 'active' ? t('prc.empty_k_body') : undefined} />
          )
        ) : (
          <Table
            view="procurement.contracts"
            head={[t('prc.c_no'), t('prc.c_subject'), t('prc.c_ends'), t('prc.c_value'), t('prc.c_invoiced'), t('prc.c_state')]}
            phoneHide={[2, 3, 4]}
            left={[0, 1]}
            rows={contracts.map((r) => ({
              key: r.id,
              onClick: () => set({ k: r.id }),
              cells: [
                <span className="tabular strong">{r.name}</span>,
                <span className="vat-party">
                  <span>{r.title}</span>
                  <span className="muted">{[r.supplier_name, r.tax_id].filter(Boolean).join(' · ')}</span>
                </span>,
                <span className={`tabular${r.late ? ' text-danger strong' : ''}`}>{r.ends ? day(r.ends) : '—'}</span>,
                <span className="tabular">{money(r.value)}</span>,
                r.state === 'draft' ? <span className="muted">—</span> : <Progress value={r.value} done={r.invoiced} />,
                <KBadge k={r} />,
              ],
            }))}
          />
        )}
      </div>

      {openP ? (
        <ProcedureDialog
          id={openP}
          purchasing={!!ov?.purchasing}
          onClose={() => set({ p: null })}
          onChange={changed}
          onContract={(from) => setForm({ kind: 'contract', from })}
          onOpenContract={(id) => set({ p: null, k: id })}
        />
      ) : null}
      {openK ? <ContractDialog id={openK} onClose={() => set({ k: null })} onChange={changed} onOpenProcedure={(id) => set({ k: null, p: id })} /> : null}
      {form?.kind === 'procedure' ? (
        <ProcedureForm
          onClose={() => setForm(null)}
          onDone={async (id) => {
            setForm(null);
            await changed();
            set({ tab: null, p: id });
          }}
        />
      ) : null}
      {form?.kind === 'contract' ? (
        <ContractForm
          from={form.from}
          purchasing={!!ov?.purchasing}
          onClose={() => setForm(null)}
          onDone={async (id) => {
            setForm(null);
            await changed();
            set({ tab: 'contracts', view: 'draft', p: null, k: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Shared pieces */

const fact = (label: string, value: React.ReactNode) =>
  value ? (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  ) : null;

function historyTitle(h: HistoryRow) {
  const d = h.data ?? {};
  switch (h.kind) {
    case 'published':
      return d.deadline ? t('prc.h_published_until', { at: dateTime(d.deadline) }) : t('prc.h_published');
    case 'bid':
      return t('prc.h_bid', { name: h.text ?? '' });
    case 'bid_late':
      return t('prc.h_bid_late', { name: h.text ?? '' });
    case 'bid_withdrawn':
      return t('prc.h_bid_withdrawn', { name: d.name ?? '' });
    case 'opened':
      return tn('prc.h_opened', Number(d.bids ?? 0));
    case 'evaluated':
      return d.score !== null && d.score !== undefined ? t('prc.h_evaluated_score', { name: d.name ?? '', amount: money(d.amount ?? 0), score: d.score }) : t('prc.h_evaluated', { name: d.name ?? '', amount: money(d.amount ?? 0) });
    case 'rejected':
      return t('prc.h_rejected', { name: d.name ?? '' });
    case 'awarded':
      return d.amount ? t('prc.h_awarded_amount', { name: d.name ?? '', amount: money(d.amount) }) : t('prc.h_awarded', { name: d.name ?? '' });
    case 'contracted':
      return t('prc.h_contracted', { name: d.contract ?? '' });
    case 'created':
      return d.procedure ? t('prc.h_created_from', { name: d.procedure }) : t('prc.h_created');
    case 'signed':
      return t('prc.h_signed', { day: d.date ? day(d.date) : '' });
    case 'amended': {
      const parts = [d.value_change ? money(d.value_change, { sign: true }) : null, d.new_end ? t('prc.h_new_end', { day: day(d.new_end) }) : null, d.reference].filter(Boolean).join(' · ');
      return t('prc.h_amended', { n: d.number ?? '', what: parts });
    }
    case 'invoice':
      return t('prc.h_invoice', { label: h.text ?? '', amount: money(d.amount ?? 0) });
    case 'invoice_cancelled':
      return t('prc.h_invoice_cancelled', { label: d.label ?? '', amount: money(d.amount ?? 0) });
    case 'file':
      return t('prc.h_file', { name: h.text ?? '' });
    default:
      return t(`prc.h_${h.kind}` as MessageKey);
  }
}

function History({ rows }: { rows: HistoryRow[] }) {
  return (
    <section className="item-moves">
      <h3>{t('prc.history')}</h3>
      <ol className="prot-history">
        {rows.map((h) => (
          <li key={h.id} className="prot-h">
            <span className="prot-h-dot" aria-hidden="true" />
            <span className="prot-h-body">
              <span className="strong">{historyTitle(h)}</span>
              {h.text && WITH_TEXT.has(h.kind) ? <span className="prot-h-text">{ownText(h.text)}</span> : null}
              <span className="muted small">{[dateTime(h.at), h.by_name].filter(Boolean).join(' · ')}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const size = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`);
const readBase64 = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });

function Files({ owner, id, files, canAdd, onChange }: { owner: 'procedure' | 'contract'; id: string; files: FileRow[]; canAdd: boolean; onChange: () => Promise<unknown> }) {
  const input = useRef<HTMLInputElement>(null);
  const { busy, run } = useSave();
  const [problem, setProblem] = useState<string | null>(null);
  const add = (list: FileList | null) => {
    const chosen = [...(list ?? [])];
    if (input.current) input.current.value = '';
    if (!chosen.length) return;
    const big = chosen.find((f) => f.size > MAX_FILE);
    if (big) return setProblem(t('prc.file_too_big', { name: big.name }));
    setProblem(null);
    void run(async () => {
      for (const f of chosen) await upload('procurement.attach', { owner, id, name: f.name, data: await readBase64(f) });
      await onChange();
    }, tn('prc.files_added', chosen.length));
  };
  const open = (fid: string) =>
    void run(async () => {
      const f = await action<{ name: string; mime: string; data: string }>('procurement.file', { id: fid });
      const bytes = Uint8Array.from(atob(f.data), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: f.mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    });
  return (
    <section className="item-moves">
      <div className="prot-history-head">
        <h3>{t('prc.files')}</h3>
        {canAdd ? (
          <>
            <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => add(e.target.files)} />
            <Button size="sm" variant="ghost" icon="plus" onClick={() => input.current?.click()} busy={busy}>
              {t('prc.add_file')}
            </Button>
          </>
        ) : null}
      </div>
      {problem ? (
        <p className="form-error" role="alert">
          <Icon name="alert" size={14} /> {problem}
        </p>
      ) : null}
      {files.length ? (
        <div className="list-card">
          {files.map((f) => (
            <button key={f.id} className="list-row compact-move" onClick={() => open(f.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name={f.mime === 'application/pdf' ? 'invoice' : 'image'} size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">{f.name}</span>
                <span className="list-sub">{[size(f.size), dateTime(f.at), f.by_name].filter(Boolean).join(' · ')}</span>
              </span>
              <Icon name="download" size={18} />
            </button>
          ))}
        </div>
      ) : (
        <p className="muted">{t(owner === 'procedure' ? 'prc.no_files_p' : 'prc.no_files_k')}</p>
      )}
    </section>
  );
}

/** A reason asked before a step that cannot be taken back. */
function ReasonDialog({ title, label, hint, button, danger, onClose, onSave }: { title: string; label: string; hint?: string; button: string; danger?: boolean; onClose: () => void; onSave: (reason: string) => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const save = async () => {
    setTried(true);
    if (!reason.trim()) return;
    if (await run(() => onSave(reason.trim()))) onClose();
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={save} busy={busy}>
            {button}
          </Button>
        </>
      }
    >
      <Field label={label} hint={hint} error={tried && !reason.trim() ? t('form.required') : null} required>
        <textarea className="input" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

/** A supplier from Purchasing, or a name and tax number typed in. */
function SupplierFields({ purchasing, suppliers, value, onChange, errors }: { purchasing: boolean; suppliers: Options['suppliers']; value: { supplier: string; name: string; nipt: string }; onChange: (v: { supplier: string; name: string; nipt: string }) => void; errors?: { name?: string | null } }) {
  const chosen = suppliers.find((s) => s.id === value.supplier);
  return (
    <div className="form-grid">
      {purchasing && suppliers.length ? (
        <Field label={t('prc.supplier')} hint={chosen ? chosen.tax_id ?? undefined : t('prc.supplier_hint')}>
          <select className="input" value={value.supplier} onChange={(e) => onChange({ ...value, supplier: e.target.value })}>
            <option value="">{t('prc.supplier_other')}</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      {!chosen ? (
        <>
          <Field label={t('prc.supplier_name')} error={errors?.name ?? null} required>
            <input className="input" maxLength={160} value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
          </Field>
          <Field label={t('prc.nipt')}>
            <input className="input tabular" maxLength={20} value={value.nipt} onChange={(e) => onChange({ ...value, nipt: e.target.value.toUpperCase() })} />
          </Field>
        </>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- One procedure */

function ProcedureDialog({
  id,
  purchasing,
  onClose,
  onChange,
  onContract,
  onOpenContract,
}: {
  id: string;
  purchasing: boolean;
  onClose: () => void;
  onChange: () => Promise<void>;
  onContract: (p: ProcDetail) => void;
  onOpenContract: (id: string) => void;
}) {
  const { data, reload } = useAction<ProcDetail>('procurement.procedure', { id });
  const { busy, run } = useSave();
  const [ask, setAsk] = useState<null | 'edit' | 'publish' | 'bid' | 'award' | 'cancel' | { evaluate: Bid } | { withdraw: Bid }>(null);
  const done = async () => {
    setAsk(null);
    await Promise.all([reload(), onChange()]);
  };
  if (!data) {
    return (
      <Dialog title={t('prc.procedure')} onClose={onClose} size="lg">
        <Skeleton h={260} />
      </Dialog>
    );
  }
  const p = data.procedure;
  const can = data.can;
  const openBids = () =>
    void run(async () => {
      await action('procurement.open_bids', { id: p.id });
      await done();
    }, t('prc.opened_done'));
  const pending = data.bids.filter((b) => b.state === 'received').length;
  const ranked = data.bids.filter((b) => b.rank).length;
  const sealed = p.state === 'draft' || p.state === 'published';

  return (
    <Dialog
      title={`${p.name} · ${p.title}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            {can.manage && (p.state === 'draft' || p.state === 'published') ? (
              <Button variant="ghost" icon="edit" onClick={() => setAsk('edit')}>
                {t('prc.edit')}
              </Button>
            ) : null}
            {can.award && p.state !== 'contracted' && p.state !== 'cancelled' ? (
              <Button variant="ghost" icon="x" onClick={() => setAsk('cancel')}>
                {t('prc.cancel_procedure')}
              </Button>
            ) : null}
          </span>
          {can.manage && p.state === 'draft' ? (
            <Button variant="primary" icon="send" onClick={() => setAsk('publish')}>
              {t('prc.publish')}
            </Button>
          ) : null}
          {can.manage && p.state === 'published' && !data.deadline_passed ? (
            <Button variant="primary" icon="plus" onClick={() => setAsk('bid')}>
              {t('prc.record_bid')}
            </Button>
          ) : null}
          {can.manage && p.state === 'published' && data.deadline_passed ? (
            <>
              <Button icon="plus" onClick={() => setAsk('bid')}>
                {t('prc.record_late')}
              </Button>
              <Button variant="primary" icon="eye" onClick={openBids} busy={busy}>
                {t('prc.open_bids')}
              </Button>
            </>
          ) : null}
          {can.award && p.state === 'evaluation' ? (
            <Button variant="primary" icon="star" onClick={() => setAsk('award')} disabled={!ranked}>
              {t('prc.award')}
            </Button>
          ) : null}
          {can.manage && p.state === 'awarded' ? (
            <Button variant="primary" icon="invoice" onClick={() => onContract(data)}>
              {t('prc.make_contract')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('prc.kind')}</span>
            <strong>{kindLabel(p.kind)}</strong>
          </div>
          <div>
            <span className="muted">{t('prc.c_estimated')}</span>
            <strong className="tabular">{money(p.estimated)}</strong>
          </div>
          <div>
            <span className="muted">{t('prc.criterion')}</span>
            <strong>{t(`prc.cr_${p.criterion}` as MessageKey)}</strong>
          </div>
          <PBadge s={p.state} late={p.state === 'published' && data.deadline_passed} />
        </div>

        {p.state === 'published' ? (
          data.deadline_passed ? (
            <p className="callout callout-warn">
              <Icon name="clock" size={16} />
              <span>{t('prc.deadline_passed', { at: dateTime(p.deadline!) })}</span>
            </p>
          ) : (
            <p className="callout">
              <Icon name="lock" size={16} />
              <span>{t('prc.sealed_note', { at: dateTime(p.deadline!) })}</span>
            </p>
          )
        ) : null}
        {p.state === 'evaluation' && pending ? (
          <p className="callout">
            <Icon name="eye" size={16} />
            <span>{tn('prc.to_evaluate', pending)}</span>
          </p>
        ) : null}
        {p.state === 'cancelled' ? (
          <p className="callout callout-warn">
            <Icon name="x" size={16} />
            <span>{t('prc.cancelled_because', { reason: p.cancel_reason ?? '' })}</span>
          </p>
        ) : null}
        {data.contract ? (
          <button className="list-row compact-move prc-link" onClick={() => onOpenContract(data.contract!.id)}>
            <span className="list-icon" aria-hidden="true">
              <Icon name="invoice" size={18} />
            </span>
            <span className="list-main">
              <span className="list-title">{t('prc.contract_x', { name: data.contract.name })}</span>
              <span className="list-sub">{t(`prc.ks_${data.contract.state}` as MessageKey)}</span>
            </span>
            <Icon name="back" size={16} className="flip" />
          </button>
        ) : null}

        <dl className="appr-facts">
          {fact(t('prc.responsible'), p.responsible_name)}
          {fact(t('prc.published_on'), p.published_on ? day(p.published_on) : null)}
          {fact(t('prc.c_deadline'), p.deadline ? dateTime(p.deadline) : null)}
          {fact(t('prc.opened_at'), p.opened_at ? dateTime(p.opened_at) : null)}
          {fact(t('prc.winner'), p.winner ? [p.winner, p.awarded_on ? day(p.awarded_on) : null].filter(Boolean).join(' · ') : null)}
          {fact(t('prc.award_reason'), p.award_reason)}
          {fact(t('prc.description'), p.description)}
        </dl>

        <section className="item-moves">
          <h3>{tn('prc.offers_n', data.bids.length)}</h3>
          {data.bids.length ? (
            <div className="list-card prc-bids">
              {[...data.bids].sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9)).map((b) => {
                const evaluable = can.manage && p.state === 'evaluation' && b.state !== 'withdrawn' && b.reason !== LATE;
                return (
                  <div key={b.id} className="list-row compact-move prc-bid">
                    {sealed ? (
                      <span className="list-icon" aria-hidden="true">
                        <Icon name="lock" size={18} />
                      </span>
                    ) : (
                      <span className={`prc-rank${b.rank === 1 ? ' first' : ''}`} aria-label={b.rank ? t('prc.rank_x', { n: b.rank }) : undefined}>
                        {b.rank ?? '–'}
                      </span>
                    )}
                    <span className="list-main">
                      <span className="list-title">{b.supplier_name}</span>
                      <span className="list-sub">
                        {[b.tax_id, t('prc.received_at', { at: dateTime(b.received_at) }), b.state === 'rejected' || b.state === 'withdrawn' ? ownText(b.reason) : null].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <span className="prc-bid-amount tabular">
                      {sealed ? null : b.amount !== null ? (
                        <>
                          <strong>{money(b.amount)}</strong>
                          {b.score !== null ? <span className="muted small">{t('prc.points', { n: b.score })}</span> : null}
                        </>
                      ) : (
                        '—'
                      )}
                    </span>
                    <StatusBadge tone={B_STATE[b.state].tone} icon={B_STATE[b.state].icon} label={t(sealed && b.state === 'received' ? 'prc.bs_sealed' : (`prc.bs_${b.state}` as MessageKey))} />
                    {evaluable ? (
                      <Button size="sm" icon="edit" onClick={() => setAsk({ evaluate: b })}>
                        {b.state === 'received' ? t('prc.evaluate') : t('prc.change')}
                      </Button>
                    ) : null}
                    {can.manage && p.state === 'published' && b.state === 'received' ? (
                      <Button size="sm" variant="ghost" icon="undo" onClick={() => setAsk({ withdraw: b })}>
                        {t('prc.withdraw')}
                      </Button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="muted">{p.state === 'draft' ? t('prc.no_bids_draft') : t('prc.no_bids')}</p>
          )}
        </section>

        <Files owner="procedure" id={p.id} files={data.files} canAdd={can.manage} onChange={reload} />
        <History rows={data.history} />
      </div>

      {ask === 'edit' ? <ProcedureForm procedure={p} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'publish' ? <PublishDialog procedure={p} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'bid' ? <BidDialog procedure={p} late={data.deadline_passed} purchasing={purchasing} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'award' ? <AwardDialog detail={data} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'cancel' ? (
        <ReasonDialog
          title={t('prc.cancel_title', { name: p.name })}
          label={t('prc.cancel_reason')}
          hint={t('prc.cancel_hint')}
          button={t('prc.cancel_procedure')}
          danger
          onClose={() => setAsk(null)}
          onSave={async (reason) => {
            await action('procurement.cancel_procedure', { id: p.id, reason });
            await done();
          }}
        />
      ) : null}
      {ask && typeof ask === 'object' && 'withdraw' in ask ? (
        <ReasonDialog
          title={t('prc.withdraw_title', { name: ask.withdraw.supplier_name })}
          label={t('prc.withdraw_reason')}
          button={t('prc.withdraw')}
          onClose={() => setAsk(null)}
          onSave={async (reason) => {
            await action('procurement.withdraw_bid', { id: ask.withdraw.id, reason });
            await done();
          }}
        />
      ) : null}
      {ask && typeof ask === 'object' && 'evaluate' in ask ? <EvaluateDialog bid={ask.evaluate} criterion={p.criterion} onClose={() => setAsk(null)} onDone={done} /> : null}
    </Dialog>
  );
}

function ProcedureForm({ procedure, onClose, onDone }: { procedure?: ProcDetail['procedure']; onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const { data: opts } = useAction<Options>('procurement.options');
  const [title, setTitle] = useState(procedure?.title ?? '');
  const [kind, setKind] = useState<Kind>(procedure?.kind ?? 'quotes');
  const [criterion, setCriterion] = useState<Criterion>(procedure?.criterion ?? 'lowest_price');
  const [estimated, setEstimated] = useState(procedure ? lek(procedure.estimated) : '');
  const [responsible, setResponsible] = useState(procedure?.responsible ?? '');
  const [deadline, setDeadline] = useState(procedure?.deadline ? localInput(new Date(procedure.deadline)) : '');
  const [description, setDescription] = useState(procedure?.description ?? '');
  const [tried, setTried] = useState(false);
  const minor = estimated.trim() ? parseMoney(estimated) : 0;
  const published = procedure?.state === 'published';
  const errors = {
    title: title.trim() ? null : t('form.required'),
    estimated: minor === null || minor < 0 ? t('prc.amount_wrong') : null,
    deadline: published && !deadline ? t('form.required') : deadline && published && procedure?.deadline && new Date(deadline) < new Date(procedure.deadline) ? t('prc.deadline_later') : null,
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    let id = procedure?.id ?? '';
    const ok = await run(async () => {
      const r = await action<{ id: string; name: string }>('procurement.save_procedure', {
        id: procedure?.id,
        title: title.trim(),
        kind,
        criterion,
        estimated: minor ?? 0,
        description: description.trim() || null,
        responsible: responsible || null,
        deadline: deadline ? new Date(deadline).toISOString() : null,
      });
      id = r.id;
      toast(procedure ? t('prc.saved') : t('prc.created_as', { name: r.name }));
    });
    if (ok) await onDone(id);
  };
  return (
    <Dialog
      title={procedure ? t('prc.edit_x', { name: procedure.name }) : t('prc.new_procedure')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon={procedure ? 'check' : 'plus'} onClick={save} busy={busy}>
            {procedure ? t('prc.save') : t('prc.create')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {!procedure ? <p className="muted">{t('prc.new_hint')}</p> : null}
        <Field label={t('prc.subject')} hint={t('prc.subject_hint')} error={tried ? errors.title : null} required>
          <input className="input" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} data-autofocus />
        </Field>
        <div className="form-grid">
          <Field label={t('prc.kind')} hint={t('prc.kind_hint')}>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {kindLabel(k)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('prc.c_estimated')} hint={t('prc.estimated_hint')} error={tried ? errors.estimated : null}>
            <input className="input tabular" inputMode="decimal" value={estimated} placeholder="0" onChange={(e) => setEstimated(e.target.value)} />
          </Field>
          <Field label={t('prc.criterion')}>
            <select className="input" value={criterion} onChange={(e) => setCriterion(e.target.value as Criterion)} disabled={published}>
              <option value="lowest_price">{t('prc.cr_lowest_price')}</option>
              <option value="best_value">{t('prc.cr_best_value')}</option>
            </select>
          </Field>
          <Field label={t('prc.responsible')}>
            <select className="input" value={responsible} onChange={(e) => setResponsible(e.target.value)}>
              <option value="">{t('prc.nobody')}</option>
              {(opts?.people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('prc.c_deadline')} hint={published ? t('prc.deadline_later') : t('prc.deadline_hint')} error={tried ? errors.deadline : null}>
            <input className="input" type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </Field>
        </div>
        <Field label={t('prc.description')} hint={t('prc.description_hint')}>
          <textarea className="input" rows={3} maxLength={4000} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function PublishDialog({ procedure, onClose, onDone }: { procedure: ProcDetail['procedure']; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [date, setDate] = useState(todayIso());
  const [deadline, setDeadline] = useState(procedure.deadline ? localInput(new Date(procedure.deadline)) : inDays(10));
  const [tried, setTried] = useState(false);
  const wrong = !deadline || new Date(deadline) <= new Date();
  const save = async () => {
    setTried(true);
    if (wrong) return;
    if (await run(() => action('procurement.publish', { id: procedure.id, date, deadline: new Date(deadline).toISOString() }), t('prc.published_done'))) await onDone();
  };
  return (
    <Dialog
      title={t('prc.publish_title', { name: procedure.name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="send" onClick={save} busy={busy}>
            {t('prc.publish')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('prc.publish_hint')}</p>
        <Field label={t('prc.published_on')}>
          <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label={t('prc.c_deadline')} hint={t('prc.deadline_hint')} error={tried && wrong ? t('prc.deadline_future') : null} required>
          <input className="input" type="datetime-local" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function BidDialog({ procedure, late, purchasing, onClose, onDone }: { procedure: ProcDetail['procedure']; late: boolean; purchasing: boolean; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data: opts } = useAction<Options>('procurement.options');
  const [who, setWho] = useState({ supplier: '', name: '', nipt: '' });
  const [at, setAt] = useState(localInput(new Date()));
  const [tried, setTried] = useState(false);
  const errors = { name: who.supplier || who.name.trim() ? null : t('form.required'), at: at && new Date(at) <= new Date(Date.now() + 60_000) ? null : t('prc.at_wrong') };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const ok = await run(
      () => action('procurement.receive_bid', { procedure: procedure.id, supplier: who.supplier || null, supplier_name: who.supplier ? null : who.name.trim(), tax_id: who.supplier ? null : who.nipt.trim() || null, received_at: new Date(Math.min(new Date(at).getTime(), Date.now())).toISOString() }),
      t('prc.bid_recorded'),
    );
    if (ok) await onDone();
  };
  return (
    <Dialog
      title={t('prc.bid_title', { name: procedure.name })}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="plus" onClick={save} busy={busy}>
            {t('prc.record_bid')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {late ? (
          <p className="callout callout-warn">
            <Icon name="clock" size={16} />
            <span>{t('prc.late_note')}</span>
          </p>
        ) : (
          <p className="muted">{t('prc.bid_hint')}</p>
        )}
        <SupplierFields purchasing={purchasing} suppliers={opts?.suppliers ?? []} value={who} onChange={setWho} errors={{ name: tried ? errors.name : null }} />
        <Field label={t('prc.arrived_at')} error={tried ? errors.at : null}>
          <input className="input" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function EvaluateDialog({ bid, criterion, onClose, onDone }: { bid: Bid; criterion: Criterion; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [valid, setValid] = useState(bid.state !== 'rejected');
  const [amount, setAmount] = useState(bid.amount !== null ? lek(bid.amount) : '');
  const [score, setScore] = useState(bid.score !== null ? String(bid.score) : '');
  const [reason, setReason] = useState(bid.state === 'rejected' ? bid.reason ?? '' : '');
  const [tried, setTried] = useState(false);
  const minor = amount.trim() ? parseMoney(amount) : null;
  const points = score.trim() ? Number(score) : null;
  const errors = {
    amount: valid && (minor === null || minor <= 0) ? t('prc.amount_wrong') : null,
    score: valid && criterion === 'best_value' && (points === null || !Number.isInteger(points) || points < 0 || points > 100) ? t('prc.score_wrong') : null,
    reason: !valid && !reason.trim() ? t('form.required') : null,
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const ok = await run(
      () => action('procurement.evaluate', { id: bid.id, valid, amount: minor, score: criterion === 'best_value' ? points : null, reason: valid ? null : reason.trim() }),
      t('prc.evaluated_done'),
    );
    if (ok) await onDone();
  };
  return (
    <Dialog
      title={t('prc.evaluate_title', { name: bid.supplier_name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('prc.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <Field label={t('prc.offer_amount')} hint={t('prc.offer_amount_hint')} error={tried ? errors.amount : null} required={valid}>
          <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-autofocus />
        </Field>
        {criterion === 'best_value' ? (
          <Field label={t('prc.score')} hint={t('prc.score_hint')} error={tried ? errors.score : null} required={valid}>
            <input className="input tabular" inputMode="numeric" value={score} onChange={(e) => setScore(e.target.value)} />
          </Field>
        ) : null}
        <label className="look-toggle">
          <span>
            <span className="strong">{t('prc.valid')}</span>
            <span className="muted small">{t('prc.valid_hint')}</span>
          </span>
          <Switch checked={valid} onChange={setValid} label={t('prc.valid')} />
        </label>
        {!valid ? (
          <Field label={t('prc.reject_reason')} error={tried ? errors.reason : null} required>
            <textarea className="input" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}

function AwardDialog({ detail, onClose, onDone }: { detail: ProcDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const ranked = detail.bids.filter((b) => b.rank).sort((a, b) => a.rank! - b.rank!);
  const pending = detail.bids.filter((b) => b.state === 'received').length;
  const [bid, setBid] = useState(ranked[0]?.id ?? '');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayIso());
  const [tried, setTried] = useState(false);
  const notFirst = bid !== ranked[0]?.id;
  const missing = notFirst && !reason.trim();
  const save = async () => {
    setTried(true);
    if (missing || pending) return;
    if (await run(() => action('procurement.award', { id: detail.procedure.id, bid, reason: reason.trim() || null, date }), t('prc.awarded_done'))) await onDone();
  };
  return (
    <Dialog
      title={t('prc.award_title', { name: detail.procedure.name })}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="star" onClick={save} busy={busy} disabled={!!pending}>
            {t('prc.award')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {pending ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{tn('prc.to_evaluate', pending)}</span>
          </p>
        ) : (
          <p className="muted">{t(detail.procedure.criterion === 'best_value' ? 'prc.award_hint_score' : 'prc.award_hint_price')}</p>
        )}
        <div className="list-card" role="radiogroup" aria-label={t('prc.winner')}>
          {ranked.map((b) => (
            <label key={b.id} className={`list-row compact-move prc-pick${bid === b.id ? ' active' : ''}`}>
              <input type="radio" name="award" checked={bid === b.id} onChange={() => setBid(b.id)} />
              <span className={`prc-rank${b.rank === 1 ? ' first' : ''}`}>{b.rank}</span>
              <span className="list-main">
                <span className="list-title">{b.supplier_name}</span>
                <span className="list-sub">{b.tax_id ?? ''}</span>
              </span>
              <span className="prc-bid-amount tabular">
                <strong>{money(b.amount ?? 0)}</strong>
                {b.score !== null ? <span className="muted small">{t('prc.points', { n: b.score })}</span> : null}
              </span>
            </label>
          ))}
        </div>
        <Field label={t('prc.award_reason')} hint={notFirst ? t('prc.award_reason_needed') : t('prc.award_reason_hint')} error={tried && missing ? t('form.required') : null} required={notFirst}>
          <textarea className="input" rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Field label={t('prc.awarded_on')}>
          <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- Contracts */

function ContractForm({ contract, from, purchasing, onClose, onDone }: { contract?: ContractDetail['contract']; from?: ProcDetail; purchasing: boolean; onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const { data: opts } = useAction<Options>('procurement.options');
  const winner = from?.bids.find((b) => b.rank && from.procedure.winner === b.supplier_name);
  const [title, setTitle] = useState(contract?.title ?? from?.procedure.title ?? '');
  const [who, setWho] = useState({ supplier: contract?.supplier ?? winner?.supplier ?? '', name: contract?.supplier_name ?? winner?.supplier_name ?? '', nipt: contract?.tax_id ?? winner?.tax_id ?? '' });
  const [value, setValue] = useState(contract ? lek(contract.value) : winner?.amount ? lek(winner.amount) : '');
  const [starts, setStarts] = useState(contract?.starts ?? '');
  const [ends, setEnds] = useState(contract?.ends ?? '');
  const [guarantee, setGuarantee] = useState(contract ? lek(contract.guarantee) : '');
  const [until, setUntil] = useState(contract?.guarantee_until ?? '');
  const [responsible, setResponsible] = useState(contract?.responsible ?? from?.procedure.responsible ?? '');
  const [note, setNote] = useState(contract?.note ?? '');
  const [line, setLine] = useState('');
  const [tried, setTried] = useState(false);
  const { data: budget } = useBudgetFor('procurement.contract', contract?.id ?? null);
  const minor = parseMoney(value);
  const g = guarantee.trim() ? parseMoney(guarantee) : 0;
  const errors = {
    title: title.trim() ? null : t('form.required'),
    name: who.supplier || who.name.trim() ? null : t('form.required'),
    value: minor === null || minor <= 0 ? t('prc.amount_wrong') : null,
    ends: starts && ends && ends < starts ? t('prc.ends_wrong') : null,
    guarantee: g === null || g < 0 ? t('prc.amount_wrong') : null,
    line: budget && !contract && budget.year?.control === 'strict' && !line ? t('bud.line_required') : null,
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    let id = contract?.id ?? '';
    const ok = await run(async () => {
      const r = await action<{ id: string; name: string }>('procurement.save_contract', {
        id: contract?.id,
        procedure: contract ? undefined : from?.procedure.id ?? null,
        title: title.trim(),
        supplier: who.supplier || null,
        supplier_name: who.supplier ? null : who.name.trim(),
        tax_id: who.supplier ? null : who.nipt.trim() || null,
        value: minor,
        starts: starts || null,
        ends: ends || null,
        guarantee: g ?? 0,
        guarantee_until: until || null,
        responsible: responsible || null,
        note: note.trim() || null,
      });
      id = r.id;
      if (!contract) await commitToLine('procurement.contract', r.id, line);
      toast(contract ? t('prc.saved') : t('prc.contract_created', { name: r.name }));
    });
    if (ok) await onDone(id);
  };
  return (
    <Dialog
      title={contract ? t('prc.edit_x', { name: contract.name }) : from ? t('prc.contract_from', { name: from.procedure.name }) : t('prc.new_contract')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon={contract ? 'check' : 'plus'} onClick={save} busy={busy}>
            {contract ? t('prc.save') : t('prc.create_contract')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {!contract ? <p className="muted">{t('prc.contract_hint')}</p> : null}
        <Field label={t('prc.subject')} error={tried ? errors.title : null} required>
          <input className="input" maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} data-autofocus />
        </Field>
        <SupplierFields purchasing={purchasing} suppliers={opts?.suppliers ?? []} value={who} onChange={setWho} errors={{ name: tried ? errors.name : null }} />
        <div className="form-grid">
          <Field label={t('prc.c_value')} hint={t('prc.value_hint')} error={tried ? errors.value : null} required>
            <input className="input tabular" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
          </Field>
          <Field label={t('prc.responsible')}>
            <select className="input" value={responsible} onChange={(e) => setResponsible(e.target.value)}>
              <option value="">{t('prc.nobody')}</option>
              {(opts?.people ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('prc.starts')} hint={t('prc.starts_hint')}>
            <input className="input" type="date" value={starts} onChange={(e) => setStarts(e.target.value)} />
          </Field>
          <Field label={t('prc.c_ends')} error={tried ? errors.ends : null}>
            <input className="input" type="date" value={ends} onChange={(e) => setEnds(e.target.value)} />
          </Field>
          <Field label={t('prc.guarantee')} hint={t('prc.guarantee_hint')} error={tried ? errors.guarantee : null}>
            <input className="input tabular" inputMode="decimal" value={guarantee} placeholder="0" onChange={(e) => setGuarantee(e.target.value)} />
          </Field>
          <Field label={t('prc.guarantee_until')}>
            <input className="input" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
          </Field>
        </div>
        {budget && !contract ? <BudgetLineSelect budget={budget} value={line} onChange={setLine} amount={minor ?? undefined} /> : null}
        {tried && errors.line ? (
          <p className="form-error" role="alert">
            <Icon name="alert" size={14} /> {errors.line}
          </p>
        ) : null}
        <Field label={t('prc.note')}>
          <textarea className="input" rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function ContractDialog({ id, onClose, onChange, onOpenProcedure }: { id: string; onClose: () => void; onChange: () => Promise<void>; onOpenProcedure: (id: string) => void }) {
  const { data, reload } = useAction<ContractDetail>('procurement.contract', { id });
  const { data: ov } = useAction<Overview>('procurement.overview');
  const [ask, setAsk] = useState<null | 'edit' | 'sign' | 'amend' | 'invoice' | 'completed' | 'terminated' | { takeOff: ContractDetail['invoices'][number] }>(null);
  const done = async () => {
    setAsk(null);
    await Promise.all([reload(), onChange()]);
  };
  if (!data) {
    return (
      <Dialog title={t('prc.contract')} onClose={onClose} size="lg">
        <Skeleton h={260} />
      </Dialog>
    );
  }
  const k = data.contract;
  const can = data.can;
  const active = k.state === 'active';
  return (
    <Dialog
      title={`${k.name} · ${k.title}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            {can.manage && k.state === 'draft' ? (
              <Button variant="ghost" icon="edit" onClick={() => setAsk('edit')}>
                {t('prc.edit')}
              </Button>
            ) : null}
            {can.award && active ? (
              <>
                <Button variant="ghost" icon="x" onClick={() => setAsk('terminated')}>
                  {t('prc.terminate')}
                </Button>
                <Button variant="ghost" icon="check" onClick={() => setAsk('completed')}>
                  {t('prc.complete')}
                </Button>
              </>
            ) : null}
          </span>
          {can.award && k.state === 'draft' ? (
            <Button variant="primary" icon="edit" onClick={() => setAsk('sign')}>
              {t('prc.sign')}
            </Button>
          ) : null}
          {can.award && active ? (
            <Button icon="layers" onClick={() => setAsk('amend')}>
              {t('prc.amend')}
            </Button>
          ) : null}
          {can.manage && active ? (
            <Button variant="primary" icon="plus" onClick={() => setAsk('invoice')} disabled={k.left <= 0}>
              {t('prc.add_invoice')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('prc.supplier')}</span>
            <strong>{k.supplier_name}</strong>
          </div>
          <div>
            <span className="muted">{t('prc.c_value')}</span>
            <strong className="tabular">{money(k.value)}</strong>
          </div>
          <div>
            <span className="muted">{t('prc.left')}</span>
            <strong className="tabular">{money(k.left)}</strong>
          </div>
          <KBadge k={k} />
        </div>

        {k.state !== 'draft' ? (
          <div className="prc-meter">
            <div className="prc-meter-track" role="img" aria-label={t('prc.invoiced_of', { done: money(k.invoiced), value: money(k.value) })}>
              <span style={{ width: `${k.value ? Math.min(100, (k.invoiced / k.value) * 100) : 0}%` }} />
            </div>
            <span className="muted small">{t('prc.invoiced_of', { done: money(k.invoiced), value: money(k.value) })}</span>
          </div>
        ) : (
          <p className="callout">
            <Icon name="edit" size={16} />
            <span>{t('prc.draft_note')}</span>
          </p>
        )}
        {k.late ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('prc.late_note_k', { day: day(k.ends!) })}</span>
          </p>
        ) : k.ending ? (
          <p className="callout callout-warn">
            <Icon name="clock" size={16} />
            <span>{t('prc.ending_note', { day: day(k.ends!) })}</span>
          </p>
        ) : null}
        {k.state === 'completed' || k.state === 'terminated' ? (
          <p className={`callout${k.state === 'terminated' ? ' callout-warn' : ''}`}>
            <Icon name={k.state === 'terminated' ? 'x' : 'check'} size={16} />
            <span>{[t(k.state === 'terminated' ? 'prc.terminated_on' : 'prc.completed_on', { day: day(k.ended_on!) }), k.end_reason].filter(Boolean).join(' · ')}</span>
          </p>
        ) : null}

        {ov?.budget ? <BudgetLineBox document="procurement.contract" record={k.id} editable={can.manage && (k.state === 'draft' || active)} onChange={done} /> : null}

        <dl className="appr-facts">
          {fact(t('prc.nipt'), k.tax_id)}
          {fact(t('prc.signed_on'), k.signed_on ? day(k.signed_on) : null)}
          {fact(t('prc.period'), k.starts || k.ends ? `${k.starts ? day(k.starts) : '…'} – ${k.ends ? day(k.ends) : '…'}` : null)}
          {fact(t('prc.original'), k.original !== k.value ? money(k.original) : null)}
          {fact(t('prc.guarantee'), k.guarantee ? [money(k.guarantee), k.guarantee_until ? t('prc.until', { day: day(k.guarantee_until) }) : null].filter(Boolean).join(' · ') : null)}
          {fact(t('prc.responsible'), k.responsible_name)}
          {fact(t('prc.note'), k.note)}
        </dl>
        {data.procedure ? (
          <button className="list-row compact-move prc-link" onClick={() => onOpenProcedure(data.procedure!.id)}>
            <span className="list-icon" aria-hidden="true">
              <Icon name="list" size={18} />
            </span>
            <span className="list-main">
              <span className="list-title">{t('prc.from_procedure', { name: data.procedure.name })}</span>
              <span className="list-sub">{data.procedure.title}</span>
            </span>
            <Icon name="back" size={16} className="flip" />
          </button>
        ) : null}

        <section className="item-moves">
          <h3>{tn('prc.invoices_n', data.invoices.filter((i) => !i.cancelled).length)}</h3>
          {data.invoices.length ? (
            <div className="list-card">
              {data.invoices.map((i) => (
                <div key={i.id} className={`list-row compact-move${i.cancelled ? ' prc-off' : ''}`}>
                  <span className="list-icon" aria-hidden="true">
                    <Icon name={i.bill ? 'bag' : 'invoice'} size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{i.label}</span>
                    <span className="list-sub">{[day(i.date), i.bill ? t('prc.from_purchasing') : null, i.cancelled ? t('prc.taken_off') : null].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="tabular strong">{money(i.amount)}</span>
                  {can.manage && active && !i.cancelled ? (
                    <Button size="sm" variant="ghost" icon="x" onClick={() => setAsk({ takeOff: i })} aria-label={t('prc.take_off_x', { label: i.label })}>
                      {t('prc.take_off')}
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">{t('prc.no_invoices')}</p>
          )}
        </section>

        {data.amendments.length ? (
          <section className="item-moves">
            <h3>{tn('prc.amendments_n', data.amendments.length)}</h3>
            <div className="list-card">
              {data.amendments.map((a) => (
                <div key={a.id} className="list-row compact-move">
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="layers" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{[t('prc.amendment_x', { n: a.number }), a.reference].filter(Boolean).join(' · ')}</span>
                    <span className="list-sub">{[day(a.date), a.reason].join(' · ')}</span>
                  </span>
                  <span className="vat-party prc-amend-what">
                    {a.value_change ? <span className="tabular strong">{money(a.value_change, { sign: true })}</span> : null}
                    {a.new_end ? <span className="muted small">{t('prc.h_new_end', { day: day(a.new_end) })}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <Files owner="contract" id={k.id} files={data.files} canAdd={can.manage} onChange={reload} />
        <History rows={data.history} />
      </div>

      {ask === 'edit' ? <ContractForm contract={k} purchasing={!!ov?.purchasing} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'sign' ? <SignDialog contract={k} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'amend' ? <AmendDialog contract={k} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'invoice' ? <InvoiceDialog detail={data} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'completed' || ask === 'terminated' ? <EndDialog contract={k} how={ask} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask && typeof ask === 'object' ? (
        <ReasonDialog
          title={t('prc.take_off_x', { label: ask.takeOff.label })}
          label={t('prc.take_off_reason')}
          hint={t('prc.take_off_hint')}
          button={t('prc.take_off')}
          danger
          onClose={() => setAsk(null)}
          onSave={async (reason) => {
            await action('procurement.cancel_invoice', { id: ask.takeOff.id, reason });
            await done();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function SignDialog({ contract, onClose, onDone }: { contract: ContractDetail['contract']; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [date, setDate] = useState(todayIso());
  const save = async () => {
    if (await run(() => action('procurement.sign', { id: contract.id, date }), t('prc.signed_done'))) await onDone();
  };
  return (
    <Dialog
      title={t('prc.sign_title', { name: contract.name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy}>
            {t('prc.sign')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('prc.sign_hint', { amount: money(contract.value), name: contract.supplier_name })}</p>
        <Field label={t('prc.signed_on')}>
          <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}

function AmendDialog({ contract, onClose, onDone }: { contract: ContractDetail['contract']; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [date, setDate] = useState(todayIso());
  const [direction, setDirection] = useState<'up' | 'down'>('up');
  const [change, setChange] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [tried, setTried] = useState(false);
  const minor = change.trim() ? parseMoney(change) : 0;
  const delta = (minor ?? 0) * (direction === 'down' ? -1 : 1);
  const after = contract.value + delta;
  const errors = {
    change: minor === null || minor < 0 ? t('prc.amount_wrong') : direction === 'down' && after < contract.invoiced ? t('prc.below_invoiced', { amount: money(contract.invoiced) }) : null,
    nothing: !delta && !newEnd ? t('prc.amend_nothing') : null,
    reason: reason.trim() ? null : t('form.required'),
  };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const ok = await run(() => action('procurement.amend', { id: contract.id, date, value_change: delta, new_end: newEnd || null, reason: reason.trim(), reference: reference.trim() || null }), t('prc.amended_done'));
    if (ok) await onDone();
  };
  return (
    <Dialog
      title={t('prc.amend_title', { name: contract.name })}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="layers" onClick={save} busy={busy}>
            {t('prc.amend')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('prc.amend_hint')}</p>
        <div className="form-grid">
          <Field label={t('prc.value_change')} hint={delta ? t('prc.new_value', { amount: money(after) }) : t('prc.value_change_hint')} error={tried ? errors.change : null}>
            <span className="prc-delta">
              <span className="segmented" role="radiogroup" aria-label={t('prc.value_change')}>
                {(['up', 'down'] as const).map((d) => (
                  <button key={d} type="button" role="radio" aria-checked={direction === d} className={direction === d ? 'active' : ''} onClick={() => setDirection(d)}>
                    <Icon name={d === 'up' ? 'plus' : 'minus'} size={14} />
                    <span className="sr-only">{t(d === 'up' ? 'prc.increase' : 'prc.decrease')}</span>
                  </button>
                ))}
              </span>
              <input className="input tabular" inputMode="decimal" value={change} placeholder="0" onChange={(e) => setChange(e.target.value)} data-autofocus />
            </span>
          </Field>
          <Field label={t('prc.new_end')} hint={contract.ends ? t('prc.now_ends', { day: day(contract.ends) }) : undefined}>
            <input className="input" type="date" value={newEnd} min={contract.starts ?? undefined} onChange={(e) => setNewEnd(e.target.value)} />
          </Field>
          <Field label={t('prc.reference')} hint={t('prc.reference_hint')}>
            <input className="input" maxLength={80} value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
          <Field label={t('prc.date')}>
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <Field label={t('prc.amend_reason')} error={tried ? errors.reason : null} required>
          <textarea className="input" rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {tried && errors.nothing ? (
          <p className="form-error" role="alert">
            <Icon name="alert" size={14} /> {errors.nothing}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function InvoiceDialog({ detail, onClose, onDone }: { detail: ContractDetail; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const k = detail.contract;
  const [source, setSource] = useState<'bill' | 'typed'>(detail.bills.length ? 'bill' : 'typed');
  const [bill, setBill] = useState(detail.bills[0]?.id ?? '');
  const [label, setLabel] = useState('');
  const [date, setDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [tried, setTried] = useState(false);
  const minor = parseMoney(amount);
  const picked = detail.bills.find((b) => b.id === bill);
  const value = source === 'bill' ? picked?.total ?? 0 : minor ?? 0;
  const errors =
    source === 'bill'
      ? { bill: picked ? null : t('form.required'), over: value > k.left ? t('prc.over_left', { amount: money(k.left) }) : null }
      : { label: label.trim() ? null : t('form.required'), amount: minor === null || minor <= 0 ? t('prc.amount_wrong') : value > k.left ? t('prc.over_left', { amount: money(k.left) }) : null };
  const save = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const ok = await run(
      () => action('procurement.add_invoice', source === 'bill' ? { contract: k.id, bill } : { contract: k.id, label: label.trim(), date, amount: minor }),
      t('prc.invoice_done'),
    );
    if (ok) await onDone();
  };
  return (
    <Dialog
      title={t('prc.invoice_title', { name: k.name })}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="plus" onClick={save} busy={busy}>
            {t('prc.add_invoice')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('prc.invoice_hint', { amount: money(k.left) })}</p>
        {detail.bills.length ? (
          <div className="segmented" role="radiogroup" aria-label={t('prc.invoice_source')}>
            {(['bill', 'typed'] as const).map((s) => (
              <button key={s} type="button" role="radio" aria-checked={source === s} className={source === s ? 'active' : ''} onClick={() => setSource(s)}>
                <Icon name={s === 'bill' ? 'bag' : 'edit'} size={16} />
                {t(s === 'bill' ? 'prc.src_bill' : 'prc.src_typed')}
              </button>
            ))}
          </div>
        ) : null}
        {source === 'bill' ? (
          <Field label={t('prc.bill')} hint={t('prc.bill_hint')} error={tried ? errors.bill ?? errors.over : null} required>
            <select className="input" value={bill} onChange={(e) => setBill(e.target.value)}>
              {detail.bills.map((b) => (
                <option key={b.id} value={b.id}>
                  BILL-{b.number}
                  {b.supplier_ref ? ` (${b.supplier_ref})` : ''} · {day(b.date)} · {money(b.total)}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <div className="form-grid">
            <Field label={t('prc.invoice_no')} error={tried ? errors.label : null} required>
              <input className="input" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} data-autofocus />
            </Field>
            <Field label={t('prc.date')}>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label={t('prc.amount')} hint={t('prc.amount_hint')} error={tried ? errors.amount : null} required>
              <input className="input tabular" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
          </div>
        )}
      </div>
    </Dialog>
  );
}

function EndDialog({ contract, how, onClose, onDone }: { contract: ContractDetail['contract']; how: 'completed' | 'terminated'; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [date, setDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const missing = how === 'terminated' && !reason.trim();
  const save = async () => {
    setTried(true);
    if (missing) return;
    if (await run(() => action('procurement.end_contract', { id: contract.id, how, date, reason: reason.trim() || null }), t(how === 'completed' ? 'prc.completed_done' : 'prc.terminated_done'))) await onDone();
  };
  return (
    <Dialog
      title={t(how === 'completed' ? 'prc.complete_title' : 'prc.terminate_title', { name: contract.name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant={how === 'terminated' ? 'danger' : 'primary'} icon={how === 'terminated' ? 'x' : 'check'} onClick={save} busy={busy}>
            {t(how === 'completed' ? 'prc.complete' : 'prc.terminate')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <p className="muted">{contract.left > 0 ? t('prc.end_left', { amount: money(contract.left) }) : t('prc.end_all')}</p>
        <Field label={t('prc.date')}>
          <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label={t(how === 'terminated' ? 'prc.terminate_reason' : 'prc.complete_note')} error={tried && missing ? t('form.required') : null} required={how === 'terminated'}>
          <textarea className="input" rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
        </Field>
      </div>
    </Dialog>
  );
}
